import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Currency, CurrencyResponse, EconomicFormat, Process, ProcessResponse, Stage, StageResponse } from '@interfaces'
import { BehaviorSubject, Observable, map } from 'rxjs'

@Injectable({
	providedIn: 'root'
})
export class ProcessesService {
	private http = inject(HttpClient)
	url = '/opportunities/process'

	defaultStages: Stage[] = [
		{ name: 'Etapa 1', colour: '#F0F0F0', orderNumber: 0 },
		{ name: 'Etapa 2', colour: '#F0F0F0', orderNumber: 1 },
		{ name: 'Etapa 3', colour: '#F0F0F0', orderNumber: 2 },
		{ name: 'Etapa 4', colour: '#F0F0F0', orderNumber: 3 }
	]

	defautlProcessValue: Process = {
		name: '',
		stages: this.defaultStages.map(stage => ({ ...stage })),
		economicFormat: {
			currency: this.getDefautlCurrency(),
			decimals: 2
		},
		system: false,
		status: 'ACTIVE'
	}

	// Observable para guardar informacion del processo y las etapas
	private _processSubject = new BehaviorSubject<Process>(this.defautlProcessValue)
	process$ = this._processSubject.asObservable()

	// crea o modifica un proceso
	createOrUpdateProcess(): Observable<Process> {
		const process: Process = {
			...this._processSubject.getValue(),
			stages: this._processSubject.getValue().stages.map((stage, index) => ({ ...stage, orderNumber: index }))
		}

		return this.http.post<Process>(`${this.url}`, process)
	}

	// Comprueba si se puede crear un proceso
	checkCreateProcess(): Observable<boolean> {
		return this.http.get<boolean>(`${this.url}/checkProcesses`)
	}

	//Elimina por completo un proceso
	deleteProcesses(processList: { id: number }[]): Observable<void> {
		return this.http.delete<void>(`${this.url}`, { body: processList })
	}

	//Marca una serie de procesos como inactivos
	disableProcesses(processList: { id: number }[]): Observable<void> {
		return this.http.post<void>(`${this.url}/status?status=INACTIVE`, processList)
	}

	//Marca una serie de procesos como activos
	enableProcesses(processList: { id: number }[]): Observable<void> {
		return this.http.post<void>(`${this.url}/status?status=ACTIVE`, processList)
	}

	// Método para obtener el tipo de monedas paginadas
	getAllCurrencies(): Observable<Currency[]> {
		return this.http.get<CurrencyResponse>(`/opportunities/currencies`).pipe(map(data => data.content))
	}

	// Método para traer el listado completo de procesos activos
	getAllActiveProcesses(): Observable<ProcessResponse> {
		return this.http.get<ProcessResponse>(`${this.url}?status=ACTIVE`)
	}

	// Método para traer el listado completo de procesos activos
	getActiveProcesses(page = 0, size = 15, inputFilter = ''): Observable<ProcessResponse> {
		const URL = `${this.url}?status=ACTIVE`
		return this.http.get<ProcessResponse>(URL, {
			params: { page, size, genericFilter: inputFilter }
		})
	}

	// Método para obtener el tipo de monedas paginadas
	getCurrencies(page?: number, size?: number): Observable<CurrencyResponse> {
		const params = page && size ? { page, size } : undefined
		return this.http.get<CurrencyResponse>(`/opportunities/currencies`, { params })
	}

	// Método para traer el detalle de un proceso el apartado economic por el id
	getDetailsEconomic(idProcess: number): Observable<EconomicFormat> {
		return this.http.get<EconomicFormat>(`${this.url}/${idProcess}/economic`)
	}

	// Método para traer el detalle de un proceso por el id
	getDetailsProcess(idProcess: number): Observable<Process> {
		return this.http.get<Process>(`${this.url}/${idProcess}`)
	}

	// Método para traer el listado de procesos paginados
	getProcesses(page = 0, size = 15, filter = { status: 'ACTIVE' }, order = 'ALPHABETIC'): Observable<ProcessResponse> {
		return this.http.get<ProcessResponse>(
			`${this.url}?status=${filter.status}&page=${page}&size=${size}&order=${order}`
		)
	}

	getStageOfProcess(idProcess: number): Observable<Stage[]> {
		return this.http
			.get<StageResponse>(`${this.url}/stages?idProcess=${idProcess}`)
			.pipe(map(response => response.content))
	}

	// Extrae del localStorage los datos de la moneda por defecto según el usuario
	getDefautlCurrency(): Currency {
		const defaultParamsString = localStorage.getItem('defaultParams')
		const defaultParams = defaultParamsString ? JSON.parse(defaultParamsString) : null
		const currency = defaultParams?.customerCountry?.currency ?? {}

		return {
			id: currency.id,
			name: currency.description,
			symbol: currency.symbol
		}
	}

	// Método para verificar si alguna etapa en el proceso actual tiene el nombre vacío
	hasEmptyStageName(): boolean {
		const process = this._processSubject.getValue()
		return process.stages.some(stage => stage.name.trim() === '')
	}

	// resetea los valores del observable de procesos
	resetProcessValue() {
		this._processSubject.next({
			...this.defautlProcessValue,
			stages: this.defaultStages.map(stage => ({ ...stage }))
		})
	}

	// Setea los valores de economicFormat seleccionados en la pestaña economic
	setEconomicValue(economic: EconomicFormat) {
		const currentProcess = this._processSubject.getValue()
		const updatedProcess: Process = {
			...currentProcess,
			economicFormat: {
				currency: economic.currency,
				decimals: economic.decimals
			}
		}

		this._processSubject.next(updatedProcess)
	}

	// Setea el nombre en el apartado proceso
	setProcessName(name: string) {
		const currentProcess = this._processSubject.getValue()
		const updatedProcess: Process = {
			...currentProcess,
			name
		}

		this._processSubject.next(updatedProcess)
	}

	// Setea los valores de una peticion en concreto obtenido por el id de la url
	setProcessValue(process: Process) {
		this._processSubject.next(process)
	}
}
